/** @typedef {import('../../types/finance').RefundPreview} RefundPreview */
/** @typedef {import('../../types/finance').Refund} Refund */
/** @typedef {import('../../types/finance').DisputeResolution} DisputeResolution */
/** @typedef {import('../../types/api').ApiResponse} ApiResponse */

import {
  acceptDispute as acceptDisputeAction,
  createRefund as createRefundAction,
  previewRefund as previewRefundAction
} from '@basqet-dashboard/common/api';
import { useMutation, useQueryClient } from 'react-query';

/**
 * Dry-run a refund. Not cached — the caller previews on demand when the refund
 * option changes, then again with wallet details before review.
 *
 * @returns {import('react-query').UseMutationResult<ApiResponse<RefundPreview>, unknown, { reference: string, refund_type: string, wallet_address?: string, wallet_network?: string }>}
 */
export const useRefundPreviewMutation = () =>
  useMutation({ mutationFn: (payload) => previewRefundAction(payload) });

/**
 * Initiate a refund. On success the disputes list is invalidated so the row
 * reflects the new (resolved/processing) state.
 *
 * @returns {import('react-query').UseMutationResult<ApiResponse<Refund>, unknown, object>}
 */
export const useCreateRefundMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createRefundAction(payload),
    onSuccess: () => queryClient.invalidateQueries('allDisputes')
  });
};

/**
 * Accept a dispute (no refund). On success the disputes list is invalidated.
 *
 * @returns {import('react-query').UseMutationResult<ApiResponse<DisputeResolution>, unknown, object>}
 */
export const useAcceptDisputeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => acceptDisputeAction(payload),
    onSuccess: () => queryClient.invalidateQueries('allDisputes')
  });
};
